import { useEffect, useState } from 'react';
import { dataSourcesApi, type DataSourceCatalogItem, type SourceStatus } from '../api/dataSourcesClient';

interface Props { onDataChange?: () => void }

const SOURCE_LABEL: Record<string, string> = { sbp_easydata: 'State Bank of Pakistan', pbs: 'PBS', world_bank: 'World Bank WDI', imf: 'IMF WEO', ilo: 'ILOSTAT', manual: 'Finance Division / FBR' };

export function DataSourcesPanel({ onDataChange }: Props) {
  const [catalog, setCatalog] = useState<DataSourceCatalogItem[]>([]);
  const [status, setStatus] = useState<SourceStatus | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');
  const load = () => Promise.all([dataSourcesApi.catalog(), dataSourcesApi.status()]).then(([c, s]) => { setCatalog(c); setStatus(s); }).catch((e) => setMsg(String(e)));
  useEffect(() => { load(); }, []);

  const run = async (source: string) => {
    setBusy(source); setMsg(null);
    try {
      const r = await dataSourcesApi.fetchSource(source);
      if (source === 'all') {
        const parts = Object.entries(r.results).map(([k, v]: [string, any]) => `${SOURCE_LABEL[k] || k}: ${v.imported} imported${v.source_errors?.length ? ` (${v.source_errors.length} errors)` : ''}`);
        setMsg(`Refreshed — ${parts.join(' · ')}`);
      } else setMsg(`${SOURCE_LABEL[source] || source}: ${r.imported} observations imported, ${r.skipped} skipped${r.source_errors?.length ? `; errors: ${r.source_errors.slice(0, 3).join('; ')}` : ''}`);
      load(); onDataChange?.();
    } catch (e) { setMsg(String(e)); } finally { setBusy(null); }
  };

  const series = (status?.series || []).filter((s) => filter === 'all' || s.source === filter);
  return (
    <div className="card" style={{ borderLeft: '4px solid #1E3A5F' }}>
      <h3>Official data sources (vetting layer)</h3>
      <p className="small">All macro series used for vetting come from authorised sources: State Bank of Pakistan (EasyData API, key required), PBS (CSV template until a public API exists), World Bank WDI, IMF WEO and ILOSTAT (open APIs). Fetches run only when you click; nothing is pulled in the background unless PM_AUTO_FETCH_ON_START is set.</p>
      {status && (
        <div className="kpi-row">
          <div className="kpi"><label>Core macro variables</label><strong>{status.coverage.with_data} / {status.coverage.core_variables}</strong><span className="sub">with an official observation</span></div>
          {Object.entries(status.observations_by_source).map(([k, v]) => <div className="kpi" key={k}><label>{k}</label><strong>{v}</strong><span className="sub">observations</span></div>)}
        </div>
      )}
      <table className="data-table">
        <thead><tr><th>Source</th><th>Tier</th><th>Access</th><th>Series</th><th>Last fetch</th><th></th></tr></thead>
        <tbody>{catalog.map((c) => {
          const lf = status?.last_fetch?.[c.id];
          const fetchable = ['world_bank', 'imf', 'ilo', 'sbp_easydata'].includes(c.id);
          return (
            <tr key={c.id}>
              <td><strong>{c.name}</strong><div className="small">{c.base_url || 'manual'}</div></td>
              <td className="small">{(c as any).tier}</td>
              <td className="small">{c.auth === 'none' ? 'open' : c.auth.startsWith('api_key') ? <>API key <a href={c.register_url} target="_blank" rel="noreferrer">(register)</a></> : c.auth}<div><span className={`flag ${c.status === 'active' ? 'ok' : c.status.startsWith('active') ? 'info' : ''}`}>{c.status.replace(/_/g, ' ')}</span></div></td>
              <td>{c.series_count}</td>
              <td className="small">{lf ? <>{lf.fetched_at}<div>{lf.imported} imported{lf.errors.length ? `, ${lf.errors.length} errors` : ''}</div></> : 'never'}</td>
              <td>{fetchable ? <button className="btn-secondary" disabled={busy !== null} onClick={() => run(c.id)}>{busy === c.id ? 'Fetching…' : 'Fetch'}</button> : c.id === 'pbs' ? <a className="small" href="https://www.pbs.gov.pk/" target="_blank" rel="noreferrer">portal</a> : <span className="small">CSV template</span>}</td>
            </tr>);
        })}</tbody>
      </table>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '10px 0' }}>
        <button className="btn-primary" disabled={busy !== null} onClick={() => run('all')}>{busy === 'all' ? 'Refreshing all sources…' : 'Refresh all open sources'}</button>
        {msg && <span className="small">{msg}</span>}
      </div>
      <h3 style={{ marginTop: 12 }}>Series mapping and latest observation <select className="field" style={{ width: 'auto', marginLeft: 8 }} value={filter} onChange={(e) => setFilter(e.target.value)}><option value="all">All sources</option>{Object.keys(SOURCE_LABEL).map((k) => <option key={k} value={k}>{SOURCE_LABEL[k]}</option>)}</select></h3>
      <div style={{ overflowX: 'auto' }}><table className="data-table">
        <thead><tr><th>Source</th><th>Variable</th><th>Series / indicator</th><th>Latest value</th><th>Period</th><th>Stored from</th></tr></thead>
        <tbody>{series.map((s, i) => (
          <tr key={`${s.source}-${s.variable_id}-${i}`}>
            <td className="small">{SOURCE_LABEL[s.source] || s.source}</td>
            <td>{s.variable_name}<div className="small">{s.variable_id}</div></td>
            <td className="small">{s.series}<div>{s.description}</div></td>
            <td>{s.latest ? <strong>{s.latest.value}</strong> : <span className="flag">no data</span>}</td>
            <td className="small">{s.latest?.observation_date || '—'}</td>
            <td className="small">{s.latest ? `${s.latest.source_agency}${s.latest.note ? ` · ${s.latest.note}` : ''}` : '—'}</td>
          </tr>))}</tbody>
      </table></div>
      {status && status.coverage.missing.length > 0 && <p className="small" style={{ marginTop: 8 }}>No official observation yet for: {status.coverage.missing.join(', ')}.</p>}
    </div>
  );
}
